import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';

import type { EsnModalSize, EsnOverlayCloseReason } from '../../../shared/types/data';
import { IconComponent } from '../../atoms/icon/icon.component';

let nextId = 0;

/**
 * The sheet a task opens in (Create Island, Invite New User, …): titled header
 * with a close glyph, a scrolling body for the form, and a footer for its
 * actions — modal over the page, dismissed by Close, Escape or the backdrop.
 */
@Component({
  selector: 'esn-modal',
  imports: [IconComponent],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
  readonly open = model(false);
  readonly title = input.required<string>();

  /**
   * Sheet width in px, one of the Figma sheet widths.
   */
  readonly size = input<EsnModalSize>(480);

  readonly closeLabel = input('Close');
  readonly closeOnBackdrop = input(true);

  /**
   * Render in the page flow without a backdrop — for previews and documentation.
   */
  readonly inline = input(false);

  readonly closed = output<EsnOverlayCloseReason>();

  protected readonly titleId = `esn-modal-${nextId++}-title`;

  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  constructor() {
    effect(() => {
      const el = this.dialog().nativeElement;
      if (this.inline()) {
        el.setAttribute('open', '');
        return;
      }
      const isOpen = el.hasAttribute('open');
      if (this.open() && !isOpen) {
        if (typeof el.showModal === 'function') el.showModal();
        else el.setAttribute('open', '');
      } else if (!this.open() && isOpen) {
        if (typeof el.close === 'function') el.close();
        else el.removeAttribute('open');
      }
    });
  }

  /**
   * Closes the sheet and tells the page why.
   */
  dismiss(reason: EsnOverlayCloseReason = 'close'): void {
    if (this.inline()) {
      this.closed.emit(reason);
      return;
    }
    if (!this.open()) return;
    this.open.set(false);
    this.closed.emit(reason);
  }

  protected handleCancel(event: Event): void {
    event.preventDefault();
    this.dismiss('escape');
  }

  /**
   * A click whose target is the <dialog> itself landed on the backdrop: the panel fills the box.
   */
  protected handleClick(event: MouseEvent): void {
    if (this.closeOnBackdrop() && event.target === this.dialog().nativeElement) this.dismiss('backdrop');
  }
}
